/**
 * 定型文マスタ管理画面 (Issue #449 / SBI-07)。
 *
 * 放送文面の「基本文」を **言語別** に編集する GUI。
 * ControlPanel API の `/phrase-templates` エンドポイント群を利用する。
 *
 * 受け入れ条件:
 *   - 「基本文に近い文章は定型文マスタを用いること」
 *   - 「定型マスターは OBA-Core で編集できること」
 *
 * 画面構成:
 *   - 左: 定型文の種別 (`PHRASE_TEMPLATE_DEFINITIONS`) の一覧。選択すると右へ反映。
 *   - 右: 選択中の種別について **言語ごとの入力欄** を縦に並べる。
 *         言語は `PHRASE_TEMPLATE_LANGUAGE_OPTIONS` (日 → 英 → 中 → 韓) に従うため、
 *         表示順が放送順序と一致する。
 *
 * ⚠️ 対応言語は **基本 4 言語 (日英中韓) のみ** (Issue #527 追補 / レビュー指摘)。
 *    話者マスタには任意テキスト放送用に 20 以上の言語があるが、それを全部並べると
 *    23 種別 × 20 言語 = 460 個の入力欄になり、編集すべき 4 言語が埋もれてしまう。
 *    バックエンドも 4 言語以外を 400 で弾く (`_coerce_phrase_template_language`)。
 *
 * 保存の考え方:
 *   - 文面を入力して「保存」すると POST (= upsert) する。同一
 *     `(template_id, language_code)` は上書きされるため、新規 / 更新を
 *     画面側で区別する必要がない。
 *   - 文面を空にして「削除」するとそのレコードを消す。削除後は放送側で
 *     **コード内既定文面 (当該言語版)** へフォールバックする (放送は止まらないし、
 *     日本語に化けることもない / Issue #527 追補)。
 */
import { Spinner } from './Spinner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/common';
import { PhraseTemplatesClient } from '../api/phraseTemplates';
import type { PhraseTemplate } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useHistoryWarning } from '../hooks/useHistoryWarning';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import { judgeRemoteChange } from '../utils/remoteChange';
import { ConfirmDialog } from './ConfirmDialog';
import { MasterHistoryLink } from './MasterHistoryLink';
import { phraseTemplateTarget } from '../pages/masterHistoryTarget';
import {
  PHRASE_TEMPLATE_DEFINITIONS,
  PHRASE_TEMPLATE_LANGUAGE_OPTIONS,
  formatPlaceholders,
  groupedPhraseTemplateDefinitions,
  type PhraseTemplateDefinition,
} from '../data/phraseTemplates';

export interface PhraseTemplateMasterScreenProps {
  config: AppConfig;
}

/** 定型文本体の最大長 (バックエンド `MAX_PHRASE_TEMPLATE_BODY_LENGTH` と一致させる) */
const MAX_BODY_LENGTH = 3000;

/** 変更通知メールのリンクで、開く定型文の種別 (`template_id`) を渡す URL クエリのキー (Issue #806) */
export const TEMPLATE_QUERY_KEY = 'template';
/** 変更通知メールのリンクで、強調する言語 (`language_key`) を渡す URL クエリのキー (Issue #806) */
export const LANGUAGE_QUERY_KEY = 'language';

/**
 * 変更通知メールのリンク (`?template=<template_id>&language=<language_key>`) から、最初に開く
 * 種別と強調する言語を決める (Issue #806)。
 *
 * 未知の種別 / 言語 (リンク先の種別が後から改名された場合など) は無視して既定の表示にする。
 *
 * ⚠️ React Router のフックではなく `window.location.search` を直接読む。この画面は
 *    Router の外でも描画できるように保っている (既存のテストも Router 無しで描画する)。
 *    初期表示だけの参照なので、URL の変更を購読する必要は無い。
 */
function readInitialFocusFromUrl(): { templateId: string | null; languageKey: string | null } {
  if (typeof window === 'undefined') return { templateId: null, languageKey: null };
  const params = new URLSearchParams(window.location.search);
  const templateId = params.get(TEMPLATE_QUERY_KEY);
  const languageKey = params.get(LANGUAGE_QUERY_KEY);
  return {
    templateId:
      templateId && PHRASE_TEMPLATE_DEFINITIONS.some((d) => d.templateId === templateId)
        ? templateId
        : null,
    languageKey:
      languageKey && PHRASE_TEMPLATE_LANGUAGE_OPTIONS.some((l) => l.key === languageKey)
        ? languageKey
        : null,
  };
}

/** API クライアントの例外を画面表示用の日本語メッセージに整形する (Issue #815) */
function formatApiError(err: unknown): string {
  return describeApiErrorMessage(err);
}

/** `(template_id, language_code)` → 定型文本体 の索引キー */
function itemKey(templateId: string, languageKey: string): string {
  return `${templateId}\u0000${languageKey}`;
}

export function PhraseTemplateMasterScreen(
  props: PhraseTemplateMasterScreenProps,
): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new PhraseTemplatesClient({ getIdToken, config }),
    [getIdToken, config],
  );
  /**
   * 削除の実行確認と、保存 (更新確定) 時の他者による更新 / 削除の確認 (Issue #765)。
   */
  const { confirmExecute, guardedUpdate, confirmDialog } = useConfirmDialog();
  /** 保存後の「変更履歴の登録に失敗」警告 (マスタ変更履歴 SBI 8)。× で閉じるまで消えない */
  const { historyWarning, reportHistory } = useHistoryWarning('phrase-template-history-warning');

  /** 変更通知メールのリンクから開かれたときの初期選択 (Issue #806)。初回のみ読む。 */
  const [initialFocus] = useState(readInitialFocusFromUrl);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    initialFocus.templateId ?? PHRASE_TEMPLATE_DEFINITIONS[0]?.templateId ?? '',
  );
  /**
   * リンクで示された言語の入力欄を強調 (フォーカス) するための保留値。一度強調したら null に戻す。
   * 再描画不要の内部状態なので state ではなく ref に持つ (effect 内での setState を避ける)。
   */
  const pendingFocusLanguageRef = useRef<string | null>(
    initialFocus.templateId ? initialFocus.languageKey : null,
  );
  /** サーバに保存されている文面 (`itemKey` → body) */
  const [saved, setSaved] = useState<Record<string, string>>({});
  /**
   * コード内既定の文面 (`itemKey` → body / Issue #527)。
   *
   * `GET /phrase-templates?include_defaults=true` が `is_default: true` で返す行。
   * DynamoDB へは保存されていないが **放送で実際に使われる文面** なので、
   * 未登録の種別でもこれを初期表示する (空欄で見せると「今なんと放送されているか」が
   * 分からず、空欄のまま保存して意図せず既定へ戻す事故につながる)。
   */
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  /**
   * 画面を開いた (一覧を取得した) 時点の更新日時 (LastUpdate / `itemKey` → updated_at)。
   * 保存確定時に他者の更新 / 削除を判定する基準 (Issue #765)。未登録 (既定文面) の行は持たない。
   */
  const [savedUpdatedAt, setSavedUpdatedAt] = useState<Record<string, string | null>>({});
  /** 入力中の文面 (`itemKey` → body)。保存済みと差があるときだけ保存ボタンを有効化する */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  /** 初期化 (全件削除) の確認ダイアログ表示状態 (Issue #631) */
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  /** 一覧取得の世代カウンタ (古いレスポンスで新しい結果を上書きしない防御) */
  const requestIdRef = useRef(0);

  const definition: PhraseTemplateDefinition | undefined = useMemo(
    () => PHRASE_TEMPLATE_DEFINITIONS.find((d) => d.templateId === selectedTemplateId),
    [selectedTemplateId],
  );

  /**
   * 変更通知メールのリンクから開かれたとき、示された言語の入力欄へスクロールしてフォーカスする
   * (「変更された箇所が画面で確認できる」ため / Issue #806)。一覧の取得後に 1 回だけ行う。
   */
  useEffect(() => {
    const language = pendingFocusLanguageRef.current;
    if (!language || loading || !definition) return;
    pendingFocusLanguageRef.current = null;
    const el = document.getElementById(`phrase-template-${definition.templateId}-${language}`);
    if (el) {
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
      el.focus();
    }
  }, [loading, definition]);

  /** 定型文一覧を取得して state に反映する。 */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      // Issue #527: 未登録種別のコード内既定文面も同梱してもらう (`is_default: true`)。
      const res = await apiClient.list(undefined, { includeDefaults: true });
      if (currentRequestId !== requestIdRef.current) return;
      const nextSaved: Record<string, string> = {};
      const nextDefaults: Record<string, string> = {};
      const nextUpdatedAt: Record<string, string | null> = {};
      res.items.forEach((item: PhraseTemplate) => {
        const key = itemKey(item.template_id, item.language_code);
        if (item.is_default) {
          // DynamoDB 未登録 = コード内既定。保存済み扱いにはしない (削除ボタンは無効)。
          nextDefaults[key] = item.body ?? '';
          return;
        }
        nextSaved[key] = item.body ?? '';
        nextUpdatedAt[key] = item.updated_at;
      });
      setSaved(nextSaved);
      setSavedUpdatedAt(nextUpdatedAt);
      setDefaults(nextDefaults);
      // 未編集の下書きはサーバ値で置き換える (再読込で最新化する)。
      // 未登録の種別は「実際に放送される既定文面」を初期表示する。
      setDrafts({ ...nextDefaults, ...nextSaved });
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      setErrorMessage(formatApiError(err));
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  useEffect(() => {
    void reload();
    return () => {
      // アンマウント時に世代を進め、進行中リクエストの結果を破棄する。
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref は DOM ではなくリクエスト世代カウンタ
      ++requestIdRef.current;
    };
  }, [reload]);

  /** 入力欄の変更を下書きへ反映する。 */
  const handleChange = useCallback((templateId: string, languageKey: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [itemKey(templateId, languageKey)]: value }));
  }, []);

  /** 1 言語ぶんの文面を保存する (POST = upsert)。他者の更新 / 削除の確認は済んでいる前提。 */
  const executeSave = useCallback(
    async (templateId: string, languageKey: string, label: string): Promise<void> => {
      const key = itemKey(templateId, languageKey);
      const body = drafts[key] ?? '';
      if (body.trim() === '') return;
      setSavingKey(key);
      setErrorMessage(null);
      setNoticeMessage(null);
      try {
        const result = await apiClient.create({
          templateId,
          languageCode: languageKey,
          body,
          label,
        });
        reportHistory(result);
        setSaved((prev) => ({ ...prev, [key]: body }));
        // 保存した時点の更新日時を、次の保存の基準にする。
        setSavedUpdatedAt((prev) => ({ ...prev, [key]: result.updated_at }));
        setNoticeMessage(`${label} の文面を保存しました`);
      } catch (err) {
        setErrorMessage(formatApiError(err));
      } finally {
        setSavingKey(null);
      }
    },
    [apiClient, drafts, reportHistory],
  );

  /**
   * 保存ボタン: 開いてから他者が更新 / 削除していないかを確認してから保存する (Issue #765)。
   * 保存ボタンは入力を変えたときだけ有効なので、ここでは常に「自分の変更あり」として扱う。
   *
   *   - 開いたとき登録済みだった行が、いま無い                  → 他者が削除 (保存しない。一覧を取り直す)
   *   - 更新日時が開いたときと違う (未登録→登録済みも含む)     → 他者が更新
   *   - 未登録のまま                                           → 確認不要
   */
  const handleSave = useCallback(
    (templateId: string, languageKey: string, label: string): void => {
      const key = itemKey(templateId, languageKey);
      const wasRegistered = Object.prototype.hasOwnProperty.call(savedUpdatedAt, key);
      const baseline = wasRegistered ? savedUpdatedAt[key] : null;
      void guardedUpdate({
        hasChanges: true,
        fetchRemoteState: async () => {
          const res = await apiClient.list(templateId);
          const current = res.items.find(
            (item) => item.language_code === languageKey && !item.is_default,
          );
          // 開いたとき未登録で、いまも未登録なら他者の操作は無い。
          if (!wasRegistered && !current) return 'unchanged';
          // 開いたとき未登録だったのに、いま登録されている = 他者が登録した (更新扱い)。
          if (!wasRegistered) return 'modified';
          return judgeRemoteChange(baseline, current);
        },
        execute: () => void executeSave(templateId, languageKey, label),
        onDeleted: () => void reload(),
      });
    },
    [apiClient, executeSave, guardedUpdate, reload, savedUpdatedAt],
  );

  /** 1 言語ぶんの文面を削除する (削除後は日本語 → 既定テンプレートへフォールバック)。 */
  const handleDelete = useCallback(
    async (templateId: string, languageKey: string, label: string): Promise<void> => {
      const key = itemKey(templateId, languageKey);
      setSavingKey(key);
      setErrorMessage(null);
      setNoticeMessage(null);
      try {
        reportHistory(await apiClient.remove(templateId, languageKey));
        setSaved((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setSavedUpdatedAt((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setDrafts((prev) => ({ ...prev, [key]: '' }));
        setNoticeMessage(`${label} の文面を削除しました`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          // 先に他者が削除済み: 削除したい状態にはなっているのでメッセージは出さず、一覧だけ更新する (Issue #765)。
          await reload();
          return;
        }
        setErrorMessage(formatApiError(err));
      } finally {
        setSavingKey(null);
      }
    },
    [apiClient, reload, reportHistory],
  );

  /**
   * 定型文マスタを初期化する (Issue #631)。
   *
   * 登録済みの定型文をすべて削除し、コード内既定文面へ戻す。成功後は一覧を
   * 再取得して、入力欄を既定文面の表示へ切り替える。
   */
  const handleReset = useCallback(async (): Promise<void> => {
    setResetting(true);
    setErrorMessage(null);
    setNoticeMessage(null);
    try {
      const res = await apiClient.reset();
      // 一括系: API が返す失敗件数 (historyFailedCount) も警告に併記される
      reportHistory(res);
      setResetConfirmOpen(false);
      await reload();
      setNoticeMessage(`定型文設定を初期化しました (${res.deleted_count} 件削除)`);
    } catch (err) {
      setResetConfirmOpen(false);
      setErrorMessage(formatApiError(err));
    } finally {
      setResetting(false);
    }
  }, [apiClient, reload, reportHistory]);

  return (
    <section
      className="section section--page list-page"
      data-testid="phrase-template-master-section"
    >
      <h2>
        定型文設定
        {loading ? (
          <>
            <Spinner />
            (読み込み中...)
          </>
        ) : (
          ''
        )}
      </h2>

      <div style={{ margin: '0 0 12px' }}>
        <button
          type="button"
          onClick={() => setResetConfirmOpen(true)}
          disabled={loading || resetting || savingKey !== null}
          data-testid="phrase-template-reset"
        >
          初期化
        </button>
        <span style={{ marginLeft: 8, fontSize: 12, color: '#555' }}>
          登録済みの定型文をすべて削除し、既定文面へ戻します
        </span>
      </div>

      {historyWarning}

      <ConfirmDialog
        open={resetConfirmOpen}
        title="定型文設定の初期化"
        message={
          '登録済みの定型文(全種別・全言語)をすべて削除し、コード内の既定文面へ戻します。\nこの操作は元に戻せません。よろしいですか?'
        }
        confirmLabel="初期化する"
        destructive
        confirmDisabled={resetting}
        cancelDisabled={resetting}
        onConfirm={() => void handleReset()}
        onCancel={() => setResetConfirmOpen(false)}
      />

      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>
        放送文面の「基本文」を<strong>日本語・英語・中国語・韓国語</strong>
        の4言語で登録します。登録した文面は次回の放送生成から使用されます。 未登録の欄には
        <strong>実際に放送されている既定文面</strong>
        が初期表示されており、4言語すべて既定文面が用意されているため空欄になることはありません。
      </p>

      {errorMessage ? (
        <div role="alert" className="error-message" data-testid="phrase-template-error">
          {errorMessage}
        </div>
      ) : null}

      {noticeMessage ? (
        <div role="status" data-testid="phrase-template-notice" style={{ color: '#0a6' }}>
          {noticeMessage}
        </div>
      ) : null}

      {/* 種別一覧と入力欄だけをスクロールさせ、タイトル・説明・エラー表示は固定する (Issue #765) */}
      <div className="list-page__body" data-testid="phrase-template-list-body">
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          {/* --- 左: 定型文の種別一覧 --- */}
          {/*
            Issue #527: 粒度別デフォルト文面などの追加で種別が増えたため、
            グループ見出し (自列車遅延 / 接続線遅延 / 乗換駅 / 運行情報) でまとめる。
            フラットに並べると目的の種別を探すのに時間がかかる。
          */}
          <nav aria-label="定型文の種別" style={{ minWidth: 260 }}>
            {groupedPhraseTemplateDefinitions().map((group) => (
              <div key={group.group} style={{ marginBottom: 12 }}>
                <h3
                  style={{ margin: '0 0 4px', fontSize: 12, color: '#555' }}
                  data-testid={`phrase-template-group-${group.group}`}
                >
                  {group.group}
                </h3>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {group.definitions.map((d) => (
                    <li key={d.templateId}>
                      <button
                        type="button"
                        onClick={() => setSelectedTemplateId(d.templateId)}
                        aria-pressed={d.templateId === selectedTemplateId}
                        data-testid={`phrase-template-tab-${d.templateId}`}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '6px 10px',
                          marginBottom: 2,
                          fontSize: 13,
                          fontWeight: d.templateId === selectedTemplateId ? 700 : 400,
                        }}
                      >
                        {d.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* --- 右: 選択中の種別の言語別入力欄 --- */}
          <div style={{ flex: 1 }}>
            {definition ? (
              <>
                <h3 style={{ margin: '0 0 4px' }}>{definition.label}</h3>
                <p style={{ margin: '0 0 4px', fontSize: 13, color: '#555' }}>
                  {definition.description}
                </p>
                <p
                  style={{ margin: '0 0 12px', fontSize: 12, color: '#555' }}
                  data-testid="phrase-template-placeholders"
                >
                  使用できるプレースホルダ: {formatPlaceholders(definition.placeholders)}
                </p>

                {/*
                  Issue #527 追補: 定型文マスタは **基本 4 言語 (日英中韓) のみ** を対象とする。
                  Polly マスタの全 20 言語を並べると 23 種別 × 20 言語 = 460 個の入力欄になり、
                  編集すべき 4 言語が埋もれて事故につながるため。
                */}
                {PHRASE_TEMPLATE_LANGUAGE_OPTIONS.map((language) => {
                  const key = itemKey(definition.templateId, language.key);
                  const draft = drafts[key] ?? '';
                  const savedBody = saved[key];
                  const dirty = draft !== (savedBody ?? '');
                  const busy = savingKey === key;

                  return (
                    <div key={language.key} style={{ marginBottom: 12 }}>
                      <label
                        htmlFor={`phrase-template-${definition.templateId}-${language.key}`}
                        style={{ display: 'block', fontWeight: 700 }}
                      >
                        {language.label}
                        {savedBody === undefined ? (
                          // Issue #527: 未登録でも入力欄には「実際に放送される既定文面」が
                          // 入っているため、そのことが分かるラベルにする。
                          <span style={{ marginLeft: 6, fontSize: 12, color: '#a60' }}>
                            {defaults[key] !== undefined
                              ? '(未登録 / 既定文面を表示中)'
                              : '(未登録)'}
                          </span>
                        ) : null}
                      </label>
                      <textarea
                        id={`phrase-template-${definition.templateId}-${language.key}`}
                        data-testid={`phrase-template-input-${definition.templateId}-${language.key}`}
                        value={draft}
                        maxLength={MAX_BODY_LENGTH}
                        rows={2}
                        onChange={(e) =>
                          handleChange(definition.templateId, language.key, e.target.value)
                        }
                        style={{ width: '100%', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <button
                          type="button"
                          disabled={!dirty || draft.trim() === '' || busy}
                          onClick={() =>
                            handleSave(definition.templateId, language.key, definition.label)
                          }
                          data-testid={`phrase-template-save-${definition.templateId}-${language.key}`}
                        >
                          {busy ? (
                            <>
                              <Spinner />
                              保存中...
                            </>
                          ) : (
                            '保存'
                          )}
                        </button>
                        <button
                          type="button"
                          disabled={savedBody === undefined || busy}
                          onClick={() =>
                            confirmExecute(
                              () =>
                                void handleDelete(
                                  definition.templateId,
                                  language.key,
                                  definition.label,
                                ),
                            )
                          }
                          data-testid={`phrase-template-delete-${definition.templateId}-${language.key}`}
                        >
                          削除
                        </button>
                        {/* 他のマスタ画面と揃え、履歴の有無に関係なく全言語に出す (履歴が無ければ履歴画面側で空表示になる) */}
                        <MasterHistoryLink
                          target={phraseTemplateTarget(definition.templateId, language.key)}
                          subject={`${definition.label} (${language.label})`}
                          testId={`phrase-template-history-${definition.templateId}-${language.key}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {confirmDialog}
    </section>
  );
}
